/// The client page's console, streamed to the editor host.
///
/// The extension shows what the client says and what the script sandbox says as
/// two logs of its own, and neither is reachable from the extension host: the
/// client runs in an iframe inside the webview, and the sandbox runs in an
/// iframe inside that. Both already arrive at this page's console — the sandbox
/// over the log port `init_sandbox` hands it — so this is where the two are
/// picked apart and sent up. Capturing here rather than in the wasm client is
/// also what puts a failure to start, a browser error and an unhandled
/// rejection in the log, since none of those pass through Rust at all.

/// Marks messages from the viewport back to the host; matches `editorBridge`.
const VIEWPORT_SOURCE = 'aether-editor-viewport';

/// Lines are batched: a script logging every tick would otherwise cost a
/// postMessage hop per line, and an output channel is only redrawn so fast.
const FLUSH_MS = 100;

/// How many lines may pile up in one stream before the oldest are dropped.
/// Only reachable if the host has stopped reading; the alternative is a buffer
/// that grows until the tab dies.
const MAX_PENDING = 5000;

/// Which log a line belongs in. The client's own console and the sandbox's are
/// kept apart because they fail in different ways: a script that throws is the
/// game's code, and anything else is the engine underneath it.
export type LogStream = 'client' | 'sandbox';

export type LogLevel = 'debug' | 'log' | 'info' | 'warn' | 'error';

interface LogEntry {
    timeMs: number;
    level: LogLevel;
    message: string;
}

interface StreamBuffer {
    entries: LogEntry[];
    /// Lines thrown away since the last flush, so a gap is reported rather than
    /// silently closed up.
    dropped: number;
}

/// The console methods as they were before patching. Everything in here logs
/// through these, so a captured line still reaches the browser's own console
/// and nothing this module writes can feed back into itself.
const original = {
    debug: console.debug.bind(console),
    log: console.log.bind(console),
    info: console.info.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
};

const buffers = new Map<LogStream, StreamBuffer>();
let flushTimer: number | undefined;
let started = false;

/// Whether there is a host to send to. Posting to `window.parent` when this
/// page *is* the top window would deliver the message straight back to the
/// listeners on this document, which log about traffic they do not recognise —
/// one captured line would then produce the next.
const hosted = () => window.parent !== window;

function record(stream: LogStream, level: LogLevel, message: string): void {
    if (!hosted()) {
        return;
    }
    let buffer = buffers.get(stream);
    if (!buffer) {
        buffer = { entries: [], dropped: 0 };
        buffers.set(stream, buffer);
    }
    if (buffer.entries.length >= MAX_PENDING) {
        buffer.entries.shift();
        buffer.dropped += 1;
    }
    buffer.entries.push({ timeMs: Date.now(), level, message });
    if (flushTimer === undefined) {
        flushTimer = window.setTimeout(flush, FLUSH_MS);
    }
}

function flush(): void {
    flushTimer = undefined;
    for (const [stream, buffer] of buffers) {
        if (buffer.entries.length === 0) {
            continue;
        }
        const entries = buffer.entries;
        buffer.entries = [];
        if (buffer.dropped > 0) {
            entries.unshift({
                timeMs: entries[0].timeMs,
                level: 'warn',
                message: `… ${buffer.dropped} lines dropped: the editor host is not keeping up`,
            });
            buffer.dropped = 0;
        }
        try {
            window.parent.postMessage(
                { source: VIEWPORT_SOURCE, events: [{ type: 'clientLog', stream, entries }] },
                '*',
            );
        } catch {
            // Nothing useful to say about a log that could not be sent, and
            // saying it through the console would be captured in turn.
        }
    }
}

/// One console argument, rendered the way a text log needs it: an error brings
/// its stack, and an object that cannot be stringified still says what it was.
function text(value: unknown): string {
    if (typeof value === 'string') {
        return value;
    }
    if (value instanceof Error) {
        return value.stack ?? `${value.name}: ${value.message}`;
    }
    if (typeof value === 'object' && value !== null) {
        try {
            return JSON.stringify(value);
        } catch {
            return String(value);
        }
    }
    return String(value);
}

/// Colour codes, as the client's `tracing` output writes them. A terminal is
/// the only thing that reads these — a browser console shows them raw and an
/// output channel is no better — so they come off on the way past.
const ANSI = /\u001b\[[0-9;]*m/g;

/// A log line, ready to read: colour codes gone, and no trailing newline —
/// `tracing` ends its lines itself, and a channel that adds its own would print
/// every one of them double-spaced.
const clean = (message: string) => message.replace(ANSI, '').replace(/\s+$/, '');

const format = (args: unknown[]) => clean(args.map(text).join(' '));

/// Start streaming this page's console to the editor host.
///
/// Called at module scope rather than once the game is up, so a client that
/// never gets that far still says why in the log.
export function startConsoleCapture(): void {
    if (started) {
        return;
    }
    started = true;

    for (const level of ['debug', 'log', 'info', 'warn', 'error'] as const) {
        console[level] = (...args: unknown[]) => {
            original[level](...args);
            record('client', level, format(args));
        };
    }

    // Uncaught errors reach the browser's console without going through the
    // console API, so they have to be listened for separately.
    window.addEventListener('error', (event) => {
        const where = event.filename ? ` (${event.filename}:${event.lineno}:${event.colno})` : '';
        record('client', 'error', `Uncaught ${text(event.error ?? event.message)}${where}`);
    });
    window.addEventListener('unhandledrejection', (event) => {
        record('client', 'error', `Unhandled rejection: ${text(event.reason)}`);
    });

    record('client', 'info', `— client loaded (${window.location.href}) —`);
}

/// Put a line the script sandbox sent in its own log.
///
/// The sandbox is a separate document and cannot reach the host, so its lines
/// arrive over the log port and are relayed from here. They are also echoed to
/// the console under a prefix, which is all a plain browser has to read them
/// with — through the unpatched console, so they land in one log rather than
/// both.
export function recordSandboxLog(level: LogLevel, message: string): void {
    original[level](`[sandbox] ${message}`);
    record('sandbox', level, clean(message));
}
